// src/pages/OrderPageWriting.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { 
  FaFileAlt, 
  FaGraduationCap, 
  FaLanguage, 
  FaFileWord, 
  FaPaperPlane,
  FaInfoCircle,
  FaClock,
  FaBook,
  FaUniversity,
  FaQuoteRight,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaCalendarDay,
  FaHourglassStart
} from "react-icons/fa";
import "./OrderPage.css";

// Mapping of writing help paths to service titles
const writingServiceTitles = {
  "/writing-help/essay": "Essay Writing Service",
  "/writing-help/research-paper": "Research Paper Writing Service",
  "/writing-help/thesis": "Thesis Writing Service",
  "/writing-help/dissertation": "Dissertation Writing Service",
  "/writing-help/assignment": "Assignment Writing Service",
  "/writing-help/book-report": "Book Report Writing Service",
  "/writing-help/speech": "Speech Writing Service",
  "/writing-help/personal-statement": "Personal Statement Writing Service",
  "/writing-help/literature-review": "Literature Review Writing Service",
  "/writing-help/coursework": "Coursework Writing Service",
  "/writing-help/lab-report": "Lab Report Writing Service",
  "/writing-help/homework": "Homework Help Service",
  "/writing-help/case-study": "Case Study Writing Service",
  "/writing-help/capstone-project": "Capstone Project Writing Service",
  "/writing-help/AI-humanizer": "Plagiarism & AI Humanizer Service",
  "/writing-help/proofreading": "Editing & Proofreading Service",
  "/writing-help/Referencing": "Referencing & Citations Service",
};

// Default fallback title
const defaultServiceTitle = "Writing Help Service";

const OrderPageWriting = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useContext(AuthContext);

  // Get the current path and determine the service title
  const currentPath = location.pathname;
  const serviceTitle = writingServiceTitles[currentPath] || defaultServiceTitle;
  
  // Also get a short name for the title field suggestion
  const getShortServiceName = () => {
    const title = writingServiceTitles[currentPath] || "Writing Service";
    return title.replace(" Writing Service", "").replace(" Service", "");
  };

  // Set page title based on service
  useEffect(() => {
    document.title = `${serviceTitle} - Research Writing`;
  }, [serviceTitle]);

  // Define all constant arrays at the top
  const academicLevels = [
    "High School", "College", "Undergraduate", "Bachelor", 
    "Master", "PhD", "Doctoral"
  ];

  const subjects = [
    "English Literature", "History", "Psychology", "Sociology",
    "Philosophy", "Political Science", "Economics", "Business",
    "Marketing", "Finance", "Computer Science", "Engineering",
    "Mathematics", "Physics", "Chemistry", "Biology",
    "Medicine", "Nursing", "Law", "Education",
    "Art History", "Music Theory", "Other"
  ];

  const languageOptions = [
    "English (US)", "English (UK)", "Spanish", "French", 
    "German", "Italian", "Portuguese", "Chinese",
    "Japanese", "Arabic", "Russian", "Other"
  ];

  const spacingOptions = ["Single", "Double", "1.5"];

  const formatOptions = ["APA", "MLA", "Chicago", "Harvard", "IEEE", "Other"];

  // Get current date/time for min values
  const getCurrentDateTime = () => {
    const now = new Date();
    // Add 6 hours for minimum deadline
    now.setHours(now.getHours() + 6);
    return now;
  };

  const getMaxDateTime = () => {
    const max = new Date();
    max.setDate(max.getDate() + 30);
    return max;
  };

  const formatDateTimeForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const parseDateTimeFromInput = (value) => {
    if (!value) return null;
    return new Date(value);
  };

  const [formData, setFormData] = useState({
    // Order Details (matches Django model)
    title: "",
    subject: "",
    academic_level: "High School",
    pages: 1,
    deadline: null,
    deadlineInput: "", // For the datetime-local input
    instructions: "",
    budget: 0,
    
    // Additional fields
    language: "English (US)",
    format: "APA",
    spacing: "Double",
    slides: 0,
    sources: 0,
    
    // Additional services
    plagiarismReport: false,
    executiveSummary: false,
    expertWriter: false,
    
    // Files
    files: [],
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [priceBreakdown, setPriceBreakdown] = useState({
    basePrice: 0,
    rushFee: 0,
    serviceFee: 0,
    total: 0
  });

  // Pre-fill title with service suggestion based on path
  useEffect(() => {
    const shortName = getShortServiceName();
    setFormData(prev => ({
      ...prev,
      title: `${shortName} Help`,
      instructions: `I need assistance with ${shortName.toLowerCase()}. Please ensure high-quality work with proper research and formatting.`
    }));
  }, [currentPath]);

  // Calculate price whenever relevant fields change
  useEffect(() => {
    calculatePrice();
  }, [
    formData.academic_level, 
    formData.pages, 
    formData.deadline,
    formData.plagiarismReport,
    formData.executiveSummary,
    formData.expertWriter,
    formData.slides
  ]);

  const calculatePrice = () => {
    // Base prices per page by academic level
    const basePrices = {
      "High School": 10,
      "College": 12,
      "Undergraduate": 15,
      "Bachelor": 18,
      "Master": 22,
      "PhD": 28,
      "Doctoral": 35
    };

    // Base price calculation
    let basePrice = (basePrices[formData.academic_level] || 15) * formData.pages;
    
    // Rush fee calculation based on deadline
    let rushFee = 0;
    if (formData.deadline) {
      const now = new Date();
      const deadline = new Date(formData.deadline);
      const hoursDifference = Math.max(1, Math.ceil((deadline - now) / (1000 * 60 * 60)));
      
      if (hoursDifference <= 24) {
        rushFee = basePrice * 0.8; // 80% for 24 hours or less
      } else if (hoursDifference <= 72) {
        rushFee = basePrice * 0.6; // 60% for 25-72 hours
      } else if (hoursDifference <= 168) { // 7 days
        rushFee = basePrice * 0.3; // 30% for 3-7 days
      }
    }

    // Additional services
    let serviceFee = 0;
    if (formData.plagiarismReport) serviceFee += 10;
    if (formData.executiveSummary) serviceFee += 15;
    if (formData.expertWriter) serviceFee += 25;
    if (formData.slides > 0) serviceFee += formData.slides * 5;

    // Calculate total
    let total = basePrice + rushFee + serviceFee;
    
    // Apply minimum price
    total = Math.max(total, 30);

    // Update form budget
    setFormData(prev => ({ ...prev, budget: total }));

    // Update price breakdown
    setPriceBreakdown({
      basePrice: Math.round(basePrice),
      rushFee: Math.round(rushFee),
      serviceFee: Math.round(serviceFee),
      total: Math.round(total)
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'deadlineInput') {
      const date = parseDateTimeFromInput(value);
      setFormData(prev => ({
        ...prev,
        deadlineInput: value,
        deadline: date
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      files: [...prev.files, ...files]
    }));
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  // Get rush fee tier description
  const getRushFeeDescription = () => {
    if (!formData.deadline) return "";
    
    const now = new Date();
    const deadline = new Date(formData.deadline);
    const hoursDifference = Math.ceil((deadline - now) / (1000 * 60 * 60));
    
    if (hoursDifference <= 24) return "Urgent (24 hours or less)";
    if (hoursDifference <= 72) return "Fast (1-3 days)";
    if (hoursDifference <= 168) return "Standard (3-7 days)";
    return "Relaxed (More than 7 days)";
  };

  // Format date for display
  const formatDateForDisplay = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setIsLoading(true);

    // Validation
    const validationErrors = {};

    if (!formData.title.trim()) {
      validationErrors.title = "Title is required";
    }

    if (!formData.subject.trim()) {
      validationErrors.subject = "Subject is required";
    }

    if (formData.pages < 1) {
      validationErrors.pages = "Must be at least 1 page";
    }

    if (!formData.deadline) {
      validationErrors.deadline = "Deadline is required";
    } else {
      const now = new Date();
      const selectedDeadline = new Date(formData.deadline);
      const minDeadline = getCurrentDateTime();
      if (selectedDeadline <= now) {
        validationErrors.deadline = "Deadline must be in the future";
      } else if (selectedDeadline < minDeadline) {
        validationErrors.deadline = "Minimum deadline is 6 hours from now";
      }
    }

    if (!formData.instructions.trim()) {
      validationErrors.instructions = "Instructions are required";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsLoading(false);
      return;
    }

    try {
      // Format deadline to ISO string for Django
      const deadlineISO = formData.deadline.toISOString();
      
      // Prepare order data
      const orderData = {
        title: formData.title,
        subject: formData.subject,
        academic_level: formData.academic_level,
        pages: formData.pages,
        deadline: deadlineISO,
        instructions: formData.instructions,
        budget: priceBreakdown.total,
        
        // Additional data
        language: formData.language,
        format: formData.format,
        spacing: formData.spacing,
        slides: formData.slides,
        sources: formData.sources,
        plagiarismReport: formData.plagiarismReport,
        executiveSummary: formData.executiveSummary,
        expertWriter: formData.expertWriter,
        
        // Price breakdown
        priceBreakdown: priceBreakdown,
        
        // Service type
        serviceType: getShortServiceName(),
        servicePage: currentPath,
        
        // Files (will be handled separately)
        files: formData.files,
      };

      console.log("Order data prepared:", orderData);
      
      // Store in session storage for later use
      sessionStorage.setItem('orderData', JSON.stringify(orderData));
      
      // Check if user is logged in
      if (isAuthenticated) {
        navigate("/order-preview", { 
          state: { 
            orderData: orderData,
            userLoggedIn: true
          } 
        });
      } else {
        navigate("/login", { 
          state: { 
            message: "Please login to continue with your order",
            redirectTo: "/order-preview",
            redirectData: orderData
          } 
        });
      }

    } catch (err) {
      console.error("Order preparation error:", err);
      setErrors(prev => ({ 
        ...prev, 
        general: "Failed to process order. Please try again." 
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="order-page-container">
      <div className="order-background-overlay"></div>
      
      <div className="order-form-container">
        {/* Header - Customized for service */}
        <div className="order-header">
          <div className="order-icon-container">
            <FaFileAlt className="order-main-icon" />
          </div>
          <h1 className="order-title">{serviceTitle}</h1>
          <p className="order-subtitle">
            Get professional help with your {getShortServiceName().toLowerCase()}. 
            Fill out the form below to get started
          </p>
        </div>

        {/* Service-specific information */}
        <div className="order-service-info">
          <div className="order-service-badge">
            <FaInfoCircle className="order-service-icon" />
            <span>Specialized {getShortServiceName()} Service</span>
          </div>
        </div>

        {/* Login Reminder */}
        {!isAuthenticated && (
          <div className="order-login-reminder">
            <FaExclamationTriangle className="order-reminder-icon" />
            <div className="order-reminder-content">
              <p>
                <strong>Not logged in?</strong> You'll be asked to login or create an account 
                after submitting this form to continue with your {getShortServiceName().toLowerCase()} order.
              </p>
            </div>
          </div>
        )}

        {/* General Error */}
        {errors.general && (
          <div className="order-error-alert">
            <FaInfoCircle className="order-error-icon" />
            <span>{errors.general}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="order-form">
          <div className="order-form-grid">
            {/* Left Column */}
            <div className="order-form-column">
              {/* Basic Information Section */}
              <div className="order-section">
                <h3 className="order-section-title">
                  <FaFileAlt className="order-section-icon" />
                  {getShortServiceName()} Details
                </h3>
                
                <div className="order-form-group">
                  <label className="order-form-label">
                    {getShortServiceName()} Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className={`order-input ${errors.title ? 'error' : ''}`}
                    placeholder={`Enter your ${getShortServiceName().toLowerCase()} title`}
                  />
                  {errors.title && (
                    <span className="order-error-text">{errors.title}</span>
                  )}
                </div>

                <div className="order-form-group">
                  <label className="order-form-label">
                    <FaGraduationCap className="order-input-icon" />
                    Academic Level
                  </label>
                  <select
                    name="academic_level"
                    value={formData.academic_level}
                    onChange={handleChange}
                    className="order-select"
                  >
                    {academicLevels.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>

                <div className="order-form-group">
                  <label className="order-form-label">
                    Subject Area *
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`order-select ${errors.subject ? 'error' : ''}`}
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(subject => (
                      <option key={subject} value={subject}>{subject}</option>
                    ))}
                  </select>
                  {errors.subject && (
                    <span className="order-error-text">{errors.subject}</span>
                  )}
                </div>

                <div className="order-form-group">
                  <label className="order-form-label">
                    {getShortServiceName()} Length *
                  </label>
                  <div className="order-length-info">
                    <input
                      type="number"
                      name="pages"
                      value={formData.pages}
                      onChange={handleChange}
                      min="1"
                      max="100"
                      className={`order-input ${errors.pages ? 'error' : ''}`}
                    />
                    <span className="order-length-unit">pages</span>
                  </div>
                  {errors.pages && (
                    <span className="order-error-text">{errors.pages}</span>
                  )}
                  <small className="order-hint">Approximately 275 words per page</small>
                </div>
              </div>

              {/* Deadline Section */}
              <div className="order-section">
                <h3 className="order-section-title">
                  <FaClock className="order-section-icon" />
                  Deadline *
                </h3>
                
                <div className="order-form-group">
                  <label className="order-form-label">
                    <FaCalendarDay className="order-input-icon" />
                    Select Deadline Date & Time
                  </label>
                  
                  <div className="datetime-input-container">
                    <input
                      type="datetime-local"
                      name="deadlineInput"
                      value={formData.deadlineInput}
                      onChange={handleChange}
                      className={`order-input datetime-input ${errors.deadline ? 'error' : ''}`}
                      min={formatDateTimeForInput(getCurrentDateTime())}
                      max={formatDateTimeForInput(getMaxDateTime())}
                      step="900" // 15 minutes
                      required
                    />
                    <FaCalendarAlt className="datetime-input-icon" />
                  </div>
                  
                  {errors.deadline && (
                    <span className="order-error-text">{errors.deadline}</span>
                  )}
                  
                  <div className="order-deadline-info">
                    <FaInfoCircle className="order-info-icon" />
                    <div className="order-info-content">
                      <p><strong>Deadline Information:</strong></p>
                      <ul>
                        <li>Minimum deadline: 6 hours from now</li>
                        <li>Maximum deadline: 30 days from now</li>
                        <li>Select both date and time for precision</li>
                        <li>Urgent orders (24 hours or less) include rush fees</li>
                      </ul>
                    </div>
                  </div>
                  
                  {formData.deadline && (
                    <div className="order-deadline-preview">
                      <FaHourglassStart className="order-preview-icon" />
                      <div className="order-preview-content">
                        <p><strong>Selected Deadline:</strong></p>
                        <p className="order-deadline-display">
                          {formatDateForDisplay(formData.deadline)}
                        </p>
                        <p className="order-deadline-timing">
                          {getRushFeeDescription()}
                          {priceBreakdown.rushFee > 0 && (
                            <span className="order-rush-badge"> (Rush Fee Applied)</span>
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Instructions Section */}
              <div className="order-section">
                <h3 className="order-section-title">
                  Detailed Requirements *
                </h3>
                
                <div className="order-form-group">
                  <textarea
                    name="instructions"
                    value={formData.instructions}
                    onChange={handleChange}
                    className={`order-textarea ${errors.instructions ? 'error' : ''}`}
                    placeholder={`Provide detailed requirements for your ${getShortServiceName().toLowerCase()}. Include topic, guidelines, formatting requirements, sources to use, and any specific requests...`}
                    rows={8}
                  />
                  {errors.instructions && (
                    <span className="order-error-text">{errors.instructions}</span>
                  )}
                  <small className="order-hint">
                    Be as detailed as possible. Include rubric, grading criteria, and any important notes specific to {getShortServiceName().toLowerCase()}.
                  </small>
                </div>
              </div>

              {/* File Upload Section */}
              <div className="order-section">
                <h3 className="order-section-title">
                  <FaFileWord className="order-section-icon" />
                  Supporting Files (Optional)
                </h3>
                
                <div className="order-file-upload">
                  <label className="order-file-label">
                    <input
                      type="file"
                      onChange={handleFileChange}
                      multiple
                      className="order-file-input"
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                    />
                    <div className="order-file-dropzone">
                      <FaFileWord className="order-file-icon" />
                      <p>Click to upload or drag & drop files</p>
                      <small>PDF, DOC, DOCX, TXT, JPG, PNG (Max 10MB each)</small>
                    </div>
                  </label>

                  {formData.files.length > 0 && (
                    <div className="order-file-list">
                      <h4>Uploaded Files:</h4>
                      {formData.files.map((file, index) => (
                        <div key={index} className="order-file-item">
                          <span className="order-file-name">{file.name}</span>
                          <span className="order-file-size">{formatFileSize(file.size)}</span>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="order-file-remove"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="order-form-column">
              {/* Formatting Requirements */}
              <div className="order-section">
                <h3 className="order-section-title">
                  <FaBook className="order-section-icon" />
                  Formatting Requirements
                </h3>
                
                <div className="order-requirements-grid">
                  <div className="order-form-group">
                    <label className="order-form-label">
                      Spacing
                    </label>
                    <select
                      name="spacing"
                      value={formData.spacing}
                      onChange={handleChange}
                      className="order-select"
                    >
                      {spacingOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <div className="order-form-group">
                    <label className="order-form-label">
                      Format Style
                    </label>
                    <select
                      name="format"
                      value={formData.format}
                      onChange={handleChange}
                      className="order-select"
                    >
                      {formatOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <div className="order-form-group">
                    <label className="order-form-label">
                      <FaLanguage className="order-input-icon" />
                      Language
                    </label>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleChange}
                      className="order-select"
                    >
                      {languageOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <div className="order-form-group">
                    <label className="order-form-label">
                      Number of Sources
                    </label>
                    <input
                      type="number"
                      name="sources"
                      value={formData.sources}
                      onChange={handleChange}
                      min="0"
                      max="50"
                      className="order-input"
                    />
                  </div>

                  <div className="order-form-group">
                    <label className="order-form-label">
                      Slides (if presentation)
                    </label>
                    <input
                      type="number"
                      name="slides"
                      value={formData.slides}
                      onChange={handleChange}
                      min="0"
                      max="50"
                      className="order-input"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Services */}
              <div className="order-section">
                <h3 className="order-section-title">
                  <FaUniversity className="order-section-icon" />
                  Additional Services
                </h3>
                
                <div className="order-services-grid">
                  <div className="order-service-item">
                    <label className="order-service-label">
                      <input
                        type="checkbox"
                        name="plagiarismReport"
                        checked={formData.plagiarismReport}
                        onChange={handleChange}
                        className="order-checkbox"
                      />
                      <span className="order-service-text">
                        <strong>Plagiarism Report</strong>
                        <small>Turnitin report included</small>
                      </span>
                      <span className="order-service-price">+$10</span>
                    </label>
                  </div>

                  <div className="order-service-item">
                    <label className="order-service-label">
                      <input
                        type="checkbox"
                        name="executiveSummary"
                        checked={formData.executiveSummary}
                        onChange={handleChange}
                        className="order-checkbox"
                      />
                      <span className="order-service-text">
                        <strong>Executive Summary</strong>
                        <small>1-page summary of key points</small>
                      </span>
                      <span className="order-service-price">+$15</span>
                    </label>
                  </div>

                  <div className="order-service-item">
                    <label className="order-service-label">
                      <input
                        type="checkbox"
                        name="expertWriter"
                        checked={formData.expertWriter}
                        onChange={handleChange}
                        className="order-checkbox"
                      />
                      <span className="order-service-text">
                        <strong>Expert Writer</strong>
                        <small>Top-rated specialist in your field</small>
                      </span>
                      <span className="order-service-price">+$25</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Price Summary */}
              <div className="order-price-summary">
                <h3 className="order-price-title">
                  <FaQuoteRight className="order-price-icon" />
                  Price Estimate
                </h3>
                
                <div className="order-price-breakdown">
                  <div className="order-price-row">
                    <span>Base Price ({formData.pages} pages)</span>
                    <span>${priceBreakdown.basePrice}</span>
                  </div>
                  
                  {priceBreakdown.rushFee > 0 && (
                    <div className="order-price-row order-price-rush">
                      <span>Rush Fee ({getRushFeeDescription()})</span>
                      <span>+${priceBreakdown.rushFee}</span>
                    </div>
                  )}
                  
                  {priceBreakdown.serviceFee > 0 && (
                    <div className="order-price-row">
                      <span>Additional Services</span>
                      <span>+${priceBreakdown.serviceFee}</span>
                    </div>
                  )}
                  
                  <div className="order-price-row order-price-total">
                    <strong>Total Estimate</strong>
                    <strong>${priceBreakdown.total}</strong>
                  </div>
                </div>

                {formData.deadline && (
                  <div className="order-deadline-summary">
                    <p><strong>Deadline:</strong> {formatDateForDisplay(formData.deadline)}</p>
                  </div>
                )}

                <div className="order-price-note">
                  <FaInfoCircle className="order-note-icon" />
                  <p>This is an estimate. Final price will be confirmed before payment.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="order-submit-container">
            <button 
              type="submit" 
              className="order-submit-button"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="order-spinner"></span>
                  Processing Order...
                </>
              ) : (
                <>
                  <FaPaperPlane className="order-button-icon" />
                  {isAuthenticated ? `Preview ${getShortServiceName()} Order` : `Submit ${getShortServiceName()} Order`}
                </>
              )}
            </button>
            
            <p className="order-note">
              {isAuthenticated ? (
                <>
                  Logged in as <strong>{user?.username || user?.email}</strong>. 
                  You'll review your {getShortServiceName().toLowerCase()} details before proceeding to payment.
                </>
              ) : (
                `You'll be asked to login or create an account to continue with your ${getShortServiceName().toLowerCase()} order.`
              )}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrderPageWriting;